import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, DownloadIcon, FilterIcon, CalendarIcon, ChevronRightIcon, XIcon, FileTextIcon } from 'lucide-react';
import { useRaastTheme, slideVariants } from './RaastShared';
interface RaastHistoryProps {
  onBack: () => void;
  onHome: () => void;
}
type Step = 'list' | 'detail';
const MOCK_HISTORY = [{
  id: 'RST-10293847',
  date: '2023-10-25T14:30:00',
  amount: 5000,
  type: 'sent',
  status: 'completed',
  name: 'Ahmed Khan',
  raastId: '03001234567'
}, {
  id: 'RST-98765432',
  date: '2023-10-24T09:15:00',
  amount: 12000,
  type: 'received',
  status: 'completed',
  name: 'Sara Ali',
  raastId: '03119876543'
}, {
  id: 'RST-56473829',
  date: '2023-10-22T18:45:00',
  amount: 3500,
  type: 'sent',
  status: 'failed',
  name: 'Imtiaz Super Market',
  raastId: 'M-1002'
}, {
  id: 'RST-11223344',
  date: '2023-10-20T11:20:00',
  amount: 8000,
  type: 'received',
  status: 'completed',
  name: 'Ali Raza',
  raastId: '03221122334'
}, {
  id: 'RST-55667788',
  date: '2023-10-18T16:05:00',
  amount: 1500,
  type: 'sent',
  status: 'completed',
  name: 'KFC',
  raastId: 'M-1003'
}, {
  id: 'RST-99887766',
  date: '2023-10-15T10:00:00',
  amount: 25000,
  type: 'received',
  status: 'completed',
  name: 'Zainab Bibi',
  raastId: '03339988776'
}];
export function RaastHistory({
  onBack,
  onHome
}: RaastHistoryProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('list');
  const [direction, setDirection] = useState(1);
  const [selectedTx, setSelectedTx] = useState<any>(null);
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, sent, received
  const [filterStatus, setFilterStatus] = useState('all'); // all, completed, failed
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  const filteredHistory = MOCK_HISTORY.filter(tx => {
    if (filterType !== 'all' && tx.type !== filterType) return false;
    if (filterStatus !== 'all' && tx.status !== filterStatus) return false;
    return true;
  });
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2101">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2102">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2103" />
      </button>
      <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2104">{title}</h1>
      <button onClick={onHome} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isDark ? 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408] text-[#5A1408] hover:bg-[#5A1408]/5'}`} data-id="element-2105">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2106" />
      </button>
    </div>;
  const renderList = () => <motion.div key="list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2107">
      {renderHeader('Transaction History', onBack)}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2108">
        <div className="flex items-center justify-between mb-6" data-id="element-2109">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${cardBg} ${borderClr}`} data-id="element-2110">
            <CalendarIcon className={`w-4 h-4 ${textSec}`} data-id="element-2111" />
            <span className={`text-[13px] font-medium ${textPri}`} data-id="element-2112">
              Last 30 Days
            </span>
          </div>
          <button onClick={() => setShowFilters(true)} className={`p-2 rounded-full border ${cardBg} ${borderClr} ${textPri}`} data-id="element-2113">
            <FilterIcon className="w-5 h-5" data-id="element-2114" />
          </button>
        </div>

        <div className="space-y-4 flex-1 overflow-y-auto" data-id="element-2115">
          {filteredHistory.length > 0 ? filteredHistory.map((tx, i) => <button key={i} onClick={() => {
          setSelectedTx(tx);
          navigateTo('detail');
        }} className={`w-full flex items-center justify-between p-4 rounded-2xl border ${cardBg} ${borderClr}`} data-id="element-2116">
                <div className="flex items-center gap-3" data-id="element-2117">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'sent' ? 'bg-[#EF4444]/10' : 'bg-[#22C55E]/10'}`} data-id="element-2118">
                    <ArrowLeftIcon className={`w-5 h-5 ${tx.type === 'sent' ? 'text-[#EF4444] rotate-45' : 'text-[#22C55E] -rotate-135'}`} data-id="element-2119" />
                  </div>
                  <div className="text-left" data-id="element-2120">
                    <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2121">
                      {tx.name}
                    </p>
                    <p className={`text-[12px] ${textSec}`} data-id="element-2122">
                      {new Date(tx.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-2123">
                  <p className={`text-[15px] font-bold ${tx.type === 'sent' ? textPri : 'text-[#22C55E]'}`} data-id="element-2124">
                    {tx.type === 'sent' ? '-' : '+'}Rs.{' '}
                    {tx.amount.toLocaleString()}
                  </p>
                  <p className={`text-[11px] font-medium ${tx.status === 'completed' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-2125">
                    {tx.status.toUpperCase()}
                  </p>
                </div>
              </button>) : <div className="text-center py-12" data-id="element-2126">
              <p className={textSec} data-id="element-2127">No transactions found.</p>
            </div>}
        </div>
      </div>

      {/* Filters Bottom Sheet */}
      <AnimatePresence data-id="element-2128">
        {showFilters && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowFilters(false)} data-id="element-2129">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} className={`w-full max-w-[430px] rounded-t-3xl p-6 pb-8 shadow-2xl ${cardBg}`} data-id="element-2130">
              <div className="flex justify-between items-center mb-6" data-id="element-2131">
                <h3 className={`text-[18px] font-bold ${textPri}`} data-id="element-2132">Filters</h3>
                <button onClick={() => setShowFilters(false)} className={textSec} data-id="element-2133">
                  <XIcon className="w-5 h-5" data-id="element-2134" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-2135">
                <div data-id="element-2136">
                  <p className={`text-[14px] font-bold mb-3 ${textPri}`} data-id="element-2137">
                    Transaction Type
                  </p>
                  <div className="flex gap-2" data-id="element-2138">
                    {['all', 'sent', 'received'].map(type => <button key={type} onClick={() => setFilterType(type)} className={`px-4 py-2 rounded-full text-[13px] font-bold capitalize border ${filterType === type ? 'bg-[#F37024] text-white border-[#F37024]' : `${inputBg} ${textSec} ${borderClr}`}`} data-id="element-2139">
                        {type}
                      </button>)}
                  </div>
                </div>
                <div data-id="element-2140">
                  <p className={`text-[14px] font-bold mb-3 ${textPri}`} data-id="element-2141">
                    Status
                  </p>
                  <div className="flex gap-2" data-id="element-2142">
                    {['all', 'completed', 'failed'].map(status => <button key={status} onClick={() => setFilterStatus(status)} className={`px-4 py-2 rounded-full text-[13px] font-bold capitalize border ${filterStatus === status ? 'bg-[#F37024] text-white border-[#F37024]' : `${inputBg} ${textSec} ${borderClr}`}`} data-id="element-2143">
                        {status}
                      </button>)}
                  </div>
                </div>
                <button onClick={() => setShowFilters(false)} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2144">
                  Apply Filters
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
  const renderDetail = () => <motion.div key="detail" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2145">
      {renderHeader('Transaction Details', () => navigateTo('list', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2146">
        <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2147">
          <div className="flex flex-col items-center mb-8 text-center" data-id="element-2148">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${selectedTx?.type === 'sent' ? 'bg-[#EF4444]/10' : 'bg-[#22C55E]/10'}`} data-id="element-2149">
              <ArrowLeftIcon className={`w-8 h-8 ${selectedTx?.type === 'sent' ? 'text-[#EF4444] rotate-45' : 'text-[#22C55E] -rotate-135'}`} data-id="element-2150" />
            </div>
            <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2151">
              Rs. {selectedTx?.amount.toLocaleString()}
            </p>
            <p className={`text-[14px] mt-1 ${selectedTx?.status === 'completed' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-2152">
              {selectedTx?.status.toUpperCase()}
            </p>
          </div>
          <div className="space-y-4" data-id="element-2153">
            <div className="flex justify-between" data-id="element-2154">
              <span className={textSec} data-id="element-2155">Reference ID</span>
              <span className={`font-bold ${textPri}`} data-id="element-2156">{selectedTx?.id}</span>
            </div>
            <div className="flex justify-between" data-id="element-2157">
              <span className={textSec} data-id="element-2158">Date & Time</span>
              <span className={`font-bold ${textPri}`} data-id="element-2159">
                {new Date(selectedTx?.date).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2160">
              <span className={textSec} data-id="element-2161">
                {selectedTx?.type === 'sent' ? 'To' : 'From'}
              </span>
              <span className={`font-bold ${textPri}`} data-id="element-2162">{selectedTx?.name}</span>
            </div>
            <div className="flex justify-between" data-id="element-2163">
              <span className={textSec} data-id="element-2164">RAAST ID</span>
              <span className={`font-bold ${textPri}`} data-id="element-2165">
                {selectedTx?.raastId}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-4 mt-6" data-id="element-2166">
          <button className={`flex-1 py-4 rounded-2xl font-bold text-[14px] flex items-center justify-center gap-2 border-2 ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2167">
            <DownloadIcon className="w-5 h-5" data-id="element-2168" /> PDF
          </button>
          <button className={`flex-1 py-4 rounded-2xl font-bold text-[14px] flex items-center justify-center gap-2 border-2 ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2169">
            <FileTextIcon className="w-5 h-5" data-id="element-2170" /> CSV
          </button>
        </div>
      </div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2171">
      {step === 'list' && renderList()}
      {step === 'detail' && renderDetail()}
    </AnimatePresence>;
}